import { useEffect, useState } from "react";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type {
  StripeCardNumberElement,
  StripeCardNumberElementChangeEvent,
  StripeElementChangeEvent,
} from "@stripe/stripe-js";
import { Input, Text } from "@ttc3k/trekker";
import { token } from "styled-system/tokens";
import { Flex, styled } from "styled-system/jsx";
import {
  isCardPostalCodeAcceptable,
  resolvePostalFormats,
} from "./isCardPostalCodeComplete";

export type StripeCardFormChange = {
  complete: boolean;
  cardElement?: StripeCardNumberElement;
  postalCode: string;
};

export type StripeCardFormLabels = {
  cardNumber: string;
  expiry: string;
  cvc: string;
  postalCode: string;
  postalPlaceholder: string;
  zipCode: string;
  zipPlaceholder: string;
};

type StripeSplitCardFieldsProps = {
  labels: StripeCardFormLabels;
  stripeRegion?: "CA" | "US";
  defaultPostalCode?: string;
  /**
   * When false the ZIP/postal field stays on the form but an empty value no
   * longer blocks completion. Anything typed is still validated so a typo
   * isn't sent to Stripe as-is. The field is deliberately styled the same
   * either way — it should not read as optional.
   */
  requirePostalCode?: boolean;
  isDisabled?: boolean;
  onChange?: (payload: StripeCardFormChange) => void;
};

/**
 * Stripe passes these styles to their components in an iframe
 * We must use non-semantic tokens to ensure the appropriate HEX values are
 * used here rather than css variables.
 * We also must use 'px' for sizes instead of 'rem'
 */
const inputStyle = {
  fontFamily: "'Figtree', sans-serif",
  fontWeight: token("fontWeights.normal"),
  fontSize: "16px",
  lineHeight: "28px",
  fontSmoothing: "antialiased",
  ":-webkit-autofill": {
    color: token("colors.orange.600"),
  },
  "::placeholder": {
    color: token("colors.ink.400"),
  },
  "::selection": {
    color: token("colors.blue.500"),
  },
};

const stripeFieldOptions = {
  style: { base: inputStyle },
};

/**
 * These are styles applied to the stripe element outside of the iframe
 */
const StripeField = styled("div", {
  base: {
    width: "full",
    "& > .StripeElement--focus": {
      borderColor: "border.dark!",
      boxShadow: "0 0 0 1px token(colors.border.dark)",
      _hover: {
        borderColor: "border.dark!",
        boxShadow: "0 0 0 1px token(colors.border.dark)!",
      },
    },
    "& > .StripeElement--invalid": {
      borderColor: "border.error!",
      boxShadow: "0 0 0 1px token(colors.border.error)",
      _hover: {
        borderColor: "border.error!",
        boxShadow: "0 0 0 1px token(colors.border.error)!",
      },
    },
    "& > .StripeElement": {
      borderWidth: "1px",
      borderColor: "border.light",
      borderRadius: "100",
      px: "150",
      py: "100",
      bg: "white",
      color: "text.dark",
      fontFamily: "body",
      fontSize: "md",
      lineHeight: "xs",
      width: "full",
      transitionDuration: "normal",
      transitionProperty: "box-shadow, border-color",
      transitionTimingFunction: "default",
      _hover: {
        borderColor: "border.mid",
        boxShadow: "0 0 0 1px token(colors.border.mid)",
      },
    },
  },
});

const postalMaxLength = {
  US: 10,
  CA: 7,
  either: 10,
  other: 12,
} as const;

/**
 * Card number / expiry / CVC as separate Stripe Elements plus our own
 * ZIP/postal input. Unlike Stripe's combined `CardElement`, this lets a
 * booker make the postal code optional (see `requirePostalCode`).
 */
export const StripeSplitCardFields = ({
  labels,
  stripeRegion = "CA",
  defaultPostalCode = "",
  requirePostalCode = true,
  isDisabled = false,
  onChange,
}: StripeSplitCardFieldsProps) => {
  const elements = useElements();
  const stripe = useStripe();
  const [postalCode, setPostalCode] = useState(defaultPostalCode);
  const [cardCountry, setCardCountry] = useState<string | undefined>();
  const [cardComplete, setCardComplete] = useState({
    number: false,
    expiry: false,
    cvc: false,
  });

  const { label: postalLabelFormat, validation: postalValidationFormat } =
    resolvePostalFormats(cardCountry, postalCode, stripeRegion);

  const emitChange = (
    nextCardComplete: typeof cardComplete,
    nextPostalCode: string,
    nextCardCountry = cardCountry,
  ) => {
    const cardElement = elements?.getElement(CardNumberElement) ?? undefined;
    const { validation } = resolvePostalFormats(
      nextCardCountry,
      nextPostalCode,
      stripeRegion,
    );
    const complete =
      nextCardComplete.number &&
      nextCardComplete.expiry &&
      nextCardComplete.cvc &&
      isCardPostalCodeAcceptable(
        nextPostalCode,
        validation,
        requirePostalCode,
      ) &&
      !!cardElement;
    onChange?.({
      complete,
      cardElement,
      postalCode: nextPostalCode,
    });
  };

  useEffect(() => {
    if (!postalCode && defaultPostalCode) {
      setPostalCode(defaultPostalCode);
      emitChange(cardComplete, defaultPostalCode);
    }
    // Prefill from customer details when the payment step mounts; emit so
    // checkout completeness includes a postal already collected earlier.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPostalCode]);

  // Split Card Elements do not include BIN country. Once number/expiry/CVC
  // are complete, mint a PaymentMethod so we can read card.country and match
  // the old Card Element ZIP vs postal label.
  useEffect(() => {
    if (
      !cardComplete.number ||
      !cardComplete.expiry ||
      !cardComplete.cvc ||
      !stripe ||
      !elements
    ) {
      setCardCountry(undefined);
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) return;

    let cancelled = false;
    stripe
      .createPaymentMethod({ type: "card", card: cardElement })
      .then(result => {
        if (cancelled) return;
        setCardCountry(result.paymentMethod?.card?.country ?? undefined);
      })
      .catch(() => {
        if (!cancelled) setCardCountry(undefined);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardComplete.number, cardComplete.expiry, cardComplete.cvc, stripe, elements]);

  useEffect(() => {
    emitChange(cardComplete, postalCode, cardCountry);
    // Re-check completeness when BIN country arrives so a prefilled ZIP/postal
    // is validated against the card, not the Stripe account region.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardCountry, requirePostalCode]);

  const handleStripeFieldChange =
    (field: keyof typeof cardComplete) =>
    (event: StripeCardNumberElementChangeEvent | StripeElementChangeEvent) => {
      const nextCardComplete = {
        ...cardComplete,
        [field]: event.complete,
      };
      setCardComplete(nextCardComplete);
      emitChange(nextCardComplete, postalCode);
    };

  const handlePostalChange = (value: string) => {
    setPostalCode(value);
    emitChange(cardComplete, value);
  };

  const postalFieldLabel =
    postalLabelFormat === "US" ? labels.zipCode : labels.postalCode;
  const postalFieldPlaceholder =
    postalLabelFormat === "US" ? labels.zipPlaceholder : labels.postalPlaceholder;

  if (isDisabled) {
    return (
      <Flex flexDir="column" gap="150" w="full">
        <Flex flexDir="column" gap="50" w="full">
          <Text visual="bodySemiBold" color="text.dark">
            {labels.cardNumber}
          </Text>
          <Input disabled />
        </Flex>
        <Flex gap="150" w="full">
          <Flex flexDir="column" gap="50" flex="1" minW="0">
            <Text visual="bodySemiBold" color="text.dark">
              {labels.expiry}
            </Text>
            <Input disabled />
          </Flex>
          <Flex flexDir="column" gap="50" flex="1" minW="0">
            <Text visual="bodySemiBold" color="text.dark">
              {labels.cvc}
            </Text>
            <Input disabled />
          </Flex>
        </Flex>
        <Flex flexDir="column" gap="50" w="full">
          <Text visual="bodySemiBold" color="text.dark">
            {postalFieldLabel}
          </Text>
          <Input disabled />
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex flexDir="column" gap="150" w="full">
      <Flex flexDir="column" gap="50" w="full">
        <Text visual="bodySemiBold" color="text.dark">
          {labels.cardNumber}
        </Text>
        <StripeField>
          <CardNumberElement
            options={{ ...stripeFieldOptions, disableLink: true }}
            onChange={handleStripeFieldChange("number")}
          />
        </StripeField>
      </Flex>
      <Flex gap="150" w="full">
        <Flex flexDir="column" gap="50" flex="1" minW="0">
          <Text visual="bodySemiBold" color="text.dark">
            {labels.expiry}
          </Text>
          <StripeField>
            <CardExpiryElement
              options={stripeFieldOptions}
              onChange={handleStripeFieldChange("expiry")}
            />
          </StripeField>
        </Flex>
        <Flex flexDir="column" gap="50" flex="1" minW="0">
          <Text visual="bodySemiBold" color="text.dark">
            {labels.cvc}
          </Text>
          <StripeField>
            <CardCvcElement
              options={stripeFieldOptions}
              onChange={handleStripeFieldChange("cvc")}
            />
          </StripeField>
        </Flex>
      </Flex>
      <Flex flexDir="column" gap="50" w="full">
        <Text visual="bodySemiBold" color="text.dark">
          {postalFieldLabel}
        </Text>
        <Input
          placeholder={postalFieldPlaceholder}
          value={postalCode}
          onChange={e => handlePostalChange(e.target.value)}
          autoComplete="postal-code"
          maxLength={postalMaxLength[postalValidationFormat]}
        />
      </Flex>
    </Flex>
  );
};
